import { forwardRef } from 'react';
import type { ButtonHTMLAttributes } from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';
/**
 * `flat` is the default modern control. `decorated` layers the shared
 * brass-edged 9-slice face (`assets/shared/ui/buttons/*.9.png`, resolved via
 * `styles/index.css` from the asset manifest) behind the same label, hover,
 * focus, and disabled behaviour — reserved for a small set of important
 * actions (docs/UI_GUIDELINES.md §1, §13). Only `primary` and `secondary`
 * have decorated art; any other variant renders flat regardless.
 */
export type ButtonEmphasis = 'flat' | 'decorated';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  emphasis?: ButtonEmphasis;
  /** Shows a busy state and disables the button, without changing its layout (APP_SPEC §36). */
  isLoading?: boolean;
}

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary:
    'border border-accent bg-interactive text-textOnBrand shadow-1 hover:bg-interactiveHover active:bg-interactiveActive',
  secondary:
    'border border-frame bg-frame text-textOnDark shadow-1 hover:bg-frameElevated active:bg-page',
  danger: 'border border-danger bg-danger text-white shadow-1 hover:bg-dangerHover',
  ghost:
    'border border-transparent bg-transparent text-textPrimary hover:border-border hover:bg-surfaceAlt',
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: 'min-h-touch px-3 py-2 text-sm',
  md: 'min-h-touch px-4 py-2 text-base',
  lg: 'min-h-[3rem] px-5 py-3 text-lg',
};

/**
 * The decorated 9-slice art is a *dark* face for every variant (emerald for
 * primary, charcoal for secondary), drawn over the button's own background,
 * so a decorated label always uses the on-dark ink — `primary`'s flat
 * `textOnBrand` is near-black and would be unreadable on it
 * (docs/UI_GUIDELINES.md §2, §9: AA contrast for normal text).
 */
const DECORATED_CLASSES: Partial<Record<ButtonVariant, string>> = {
  primary: 'px-btn-decorated px-btn-decorated--primary !text-textOnDark',
  secondary: 'px-btn-decorated px-btn-decorated--secondary !text-textOnDark',
};

/** Accessible base button (TECH_STACK §16). Click/tap primary; no hover-only functionality (APP_SPEC §34). */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    emphasis = 'flat',
    isLoading = false,
    disabled,
    className = '',
    children,
    type = 'button',
    ...rest
  },
  ref,
) {
  const decorated = emphasis === 'decorated' ? (DECORATED_CLASSES[variant] ?? '') : '';
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled ?? isLoading}
      aria-busy={isLoading || undefined}
      className={`interactive-lift inline-flex items-center justify-center gap-2 rounded-md font-semibold transition-colors duration-fast ease-standard disabled:cursor-not-allowed disabled:border-interactiveDisabled disabled:bg-interactiveDisabled disabled:text-textPrimary disabled:opacity-75 ${VARIANT_CLASSES[variant]} ${SIZE_CLASSES[size]} ${decorated} ${className}`}
      {...rest}
    >
      {isLoading && (
        <span
          aria-hidden="true"
          className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
        />
      )}
      {children}
    </button>
  );
});
